import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { inr, CONTACT } from "@/lib/api";
import { startingPrice, ALL_SERVICES } from "@/lib/catalog";
import { ConsultNotice } from "@/components/ConsultNotice";
import { waFor } from "@/components/FloatingWhatsApp";
import { track } from "@/lib/analytics";

const LINES = [["Grow Your Business"], ["Online with", "AI-Powered"], ["Digital Solutions"]];
const ease = [0.16, 1, 0.3, 1];

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "110%", rotate: 3 }} animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease }}>
      {children}
    </motion.span>
  </span>
);

const TiltFrame = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-tilt-frame">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.6, ease }}
        className="relative aspect-[4/5] w-full">
        <div className="hero-clip absolute inset-0 overflow-hidden rounded-[2rem] border border-white/10">
          <img src="/images/hero.jpg" alt="Analytics dashboard on a laptop" className="h-full w-full scale-110 object-cover saturate-[0.7]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070B14] via-[#070B14]/30 to-transparent" />
          <div className="absolute inset-0 spotlight" />
        </div>
        <div style={{ transform: "translateZ(70px)" }}
          className="absolute -left-6 bottom-10 rounded-2xl border border-white/10 bg-[#0E1526]/85 p-4 backdrop-blur-xl sm:-left-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Live signal</p>
          <svg viewBox="0 0 160 40" className="mt-2 h-10 w-40"><path d="M0 26h30l8-14 12 24 10-18 8 8h22l10-20 12 30 10-14h38" className="pulse-draw-long" stroke="#00F0FF" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>
        </div>
        <div style={{ transform: "translateZ(50px)" }}
          className="absolute -right-3 top-8 rounded-2xl border border-white/10 bg-[#0E1526]/85 px-4 py-3 backdrop-blur-xl sm:-right-8">
          <p className="font-mono text-2xl font-bold text-white">{ALL_SERVICES.length}</p>
          <p className="text-xs text-slate-400">services, 4 packages</p>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = ({ onConsult }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 120]);
  return (
    <section className="relative overflow-hidden pt-36 pb-20 sm:pt-44 lg:min-h-screen" data-testid="hero-section">
      <div className="hero-grid pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute -top-40 left-1/3 h-[600px] w-[600px] rounded-full bg-[#00F0FF]/10 blur-[140px]" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }} className="mb-6">
            <ConsultNotice testId="hero-consult-notice" />
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF]">
            {CONTACT.company} — Pune
          </motion.p>
          <h1 className="font-display text-[2.15rem] font-extrabold leading-[1.04] tracking-tight text-white sm:text-5xl lg:text-[3.6rem] xl:text-[4rem]" data-testid="hero-title">
            {LINES.map((parts, i) => (
              <MaskLine key={i} i={i}>
                {parts.length > 1 ? <>{parts[0]} <span className="text-outline whitespace-nowrap">{parts[1]}</span></> : parts[0]}
              </MaskLine>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9, ease }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="hero-subtitle">
            We help small businesses, startups, local shops and service providers build a strong digital presence, attract potential customers and simplify business operations through professional websites, local SEO, AEO and AI automation.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9, ease }}
            className="mt-10 flex flex-wrap gap-3">
            <button onClick={() => onConsult("Free Website Audit", "hero")} data-testid="hero-cta-audit-button"
              className="group flex items-center gap-2 rounded-full bg-[#00F0FF] px-6 py-3.5 font-semibold text-[#070B14] transition-transform hover:scale-[1.03]">
              Get a Free Website Audit <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </button>
            <a href={waFor()} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { location: "hero" })} data-testid="hero-cta-whatsapp-button"
              className="flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 font-semibold text-[#05301A] transition-transform hover:scale-[1.03]">
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </motion.div>
          <motion.dl initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
            className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-white/10 pt-6">
            {[[String(ALL_SERVICES.length), "Individual services"], [inr(startingPrice), "Starting price"], ["4", "All-inclusive packages"]].map(([v, l]) => (
              <div key={l}><dt className="font-mono text-2xl font-bold text-white sm:text-3xl">{v}</dt><dd className="mt-1 text-xs text-slate-400 sm:text-sm">{l}</dd></div>
            ))}
          </motion.dl>
        </div>
        <motion.div style={{ y }} className="mx-auto w-full max-w-md lg:col-span-5 lg:mt-6">
          <TiltFrame />
        </motion.div>
      </div>
    </section>
  );
};
